import React, { useState, useEffect } from 'react';
import { 
  Construction, 
  HelpCircle, 
  MessageCircle, 
  Phone, 
  Mail, 
  Clock,
  ChevronDown,
  ChevronUp,
  Search,
  ArrowRight
} from 'lucide-react';
import { API_URL } from '../api';

interface GenericPageProps {
  title: string;
  description?: string;
}

interface StoreSettings {
  supportEmail: string;
  supportPhone: string;
}

// FAQ data with dynamic contact
const getFaqs = (phone: string, email: string) => [
  {
    question: 'How do I track my order?',
    answer: 'You can track your order by clicking on "Track Order" in the footer or going to My Orders in your profile. Enter your order number to see real-time updates.'
  },
  {
    question: 'What is your return policy?',
    answer: 'We offer a 30-day return policy for most items. Products must be unused and in original packaging. Simply initiate a return from your order history.'
  },
  {
    question: 'How long does shipping take?',
    answer: 'Standard shipping takes 5-7 business days. Express shipping (2-3 days) is available at checkout for most locations.'
  },
  {
    question: 'Do you offer cash on delivery?',
    answer: 'Yes, Cash on Delivery (COD) is available for orders under ₹10,000. A small COD fee may apply.'
  },
  {
    question: 'How can I contact customer support?',
    answer: `You can reach us via phone at ${phone}, email at ${email}, or use our live chat feature available 24/7.`
  }
];

const GenericPage: React.FC<GenericPageProps> = ({ title, description }) => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [storeSettings, setStoreSettings] = useState<StoreSettings>({
    supportEmail: 'support@nlsstore.com',
    supportPhone: '+1 (555) 123-4567'
  });

  // Fetch store settings with polling for real-time updates
  useEffect(() => {
    const fetchStoreSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/admin/ultra-settings/public/store`);
        const data = await res.json();
        if (data.success && data.data) {
          setStoreSettings({
            supportEmail: data.data.supportEmail || data.data.storeEmail || 'support@nlsstore.com',
            supportPhone: data.data.supportPhone || '+1 (555) 123-4567'
          });
        }
      } catch (error) {
        console.error('Error fetching store settings:', error);
      }
    };
    
    fetchStoreSettings();
    const intervalId = setInterval(fetchStoreSettings, 5000);
    return () => clearInterval(intervalId);
  }, []);

  const isHelpPage = title.toLowerCase().includes('help') || title.toLowerCase().includes('support');
  const faqs = getFaqs(storeSettings.supportPhone, storeSettings.supportEmail);

  // FAQ accordion toggle
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Filter FAQs based on search
  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <main className="flex-grow">
        {isHelpPage ? (
          <>
            {/* Hero Section */}
            <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-16 md:py-24">
              <div className="container mx-auto px-4">
                <div className="max-w-3xl mx-auto text-center">
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                    <HelpCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">24/7 Support Available</span>
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold mb-4">How can we help you?</h1>
                  <p className="text-xl text-blue-100 mb-8">Find answers to common questions or contact our support team</p>
                  
                  {/* Search Bar */}
                  <div className="relative max-w-xl mx-auto">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search for help..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-white/30 shadow-lg"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Quick Support Cards */}
            <section className="py-12 -mt-8">
              <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {/* Live Chat Card */}
                  <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                    <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                      <MessageCircle className="w-7 h-7 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Live Chat</h3>
                    <p className="text-gray-600 mb-4">Chat with our support team in real-time</p>
                    <button className="flex items-center gap-2 text-green-600 font-semibold hover:gap-3 transition-all">
                      Start Chat <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>

              {/* Phone Support Card */}
                  <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                      <Phone className="w-7 h-7 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Phone Support</h3>
                    <p className="text-gray-600 mb-4">Call us at {storeSettings.supportPhone}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>Available 24/7</span>
                    </div>
                  </div>

              {/* Email Support Card */}
                  <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                    <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                      <Mail className="w-7 h-7 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Email Us</h3>
                    <p className="text-gray-600 mb-4">{storeSettings.supportEmail}</p>
                    <span className="text-sm text-gray-500">Response within 24 hours</span>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-3xl mx-auto">
                  <div className="text-center mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
                    <p className="text-gray-600">Find quick answers to common questions</p>
                  </div>

                  <div className="space-y-4">
                    {filteredFaqs.map((faq, index) => (
                      <div 
                        key={index}
                        className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                      >
                        <button
                          onClick={() => toggleFaq(index)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-gray-900">{faq.question}</span>
                          {openFaq === index ? (
                            <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                        </button>
                        {openFaq === index && (
                          <div className="px-5 pb-5">
                            <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {filteredFaqs.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No results found. Try a different search term.</p>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Contact CTA */}
            <section className="py-16 bg-gradient-to-r from-indigo-600 to-blue-700">
              <div className="container mx-auto px-4 text-center">
                <h2 className="text-3xl font-bold text-white mb-4">Still need help?</h2>
                <p className="text-indigo-100 mb-8 max-w-xl mx-auto">
                  Our dedicated support team is always ready to assist you with any questions or concerns
                </p>
                <button className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors shadow-lg">
                  Contact Support Team
                </button>
              </div>
            </section>
          </>
        ) : (
          // Default view for other generic pages
          <div className="bg-gray-50 py-20">
            <div className="container mx-auto px-4 text-center">
              <div className="max-w-md mx-auto">
                <Construction className="w-16 h-16 text-blue-600 mx-auto mb-6" />
                <h1 className="text-4xl font-bold text-gray-800 mb-4">{title}</h1>
                <p className="text-xl text-gray-600 mb-8">
                  {description || 'This page is currently under development. Please check back soon!'}
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <p className="text-blue-800">
                    We're working hard to bring you the best shopping experience.
                    In the meantime, feel free to browse our products or contact our support team.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default GenericPage;